import React, { useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { apiErr } from "@/lib/api";
import { WordMark } from "@/components/brand";
import { SECTION_IMG } from "@/constants/media";
import AuthNav from "@/components/site/AuthNav";

const inputCls = "w-full bg-black/40 border border-white/10 rounded-md px-3 py-2.5 text-sm text-foreground focus:border-gold focus:outline-none focus:ring-1 focus:ring-gold/40";

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [f, setF] = useState({ first_name: "", last_name: "", email: "", password: "", phone: "", invitation_code: params.get("invite") || "", why_join: "", preferred_dates: "", interest: "" });
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const hasInvite = !!f.invitation_code;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await register(f);
      if (!u.is_staff && u.account_status && u.account_status !== "Active") {
        toast.success("Your request has been received.");
        nav("/pending");
        return;
      }
      toast.success("Welcome to The D.");
      nav("/portal");
    } catch (err) { toast.error(apiErr(err)); } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <AuthNav />
      <div className="flex items-center justify-center p-8 bg-ink order-2 lg:order-1 pt-24 lg:pt-8">
        <div className="w-full max-w-sm">
          <Link to="/"><WordMark size="text-4xl" /></Link>
          <h1 className="font-serif text-3xl mt-10 mb-1 text-center">{hasInvite ? "Redeem Your Invitation" : "Request Access"}</h1>
          <p className="text-sm text-muted-foreground text-center mb-8">
            The D is invitation-only. {hasInvite ? "Complete your details to activate your invitation." : "Submit a request and our team will review it personally."}
          </p>
          <form onSubmit={submit} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <input required placeholder="First name" className={inputCls} value={f.first_name} onChange={(e) => set("first_name", e.target.value)} data-testid="reg-first" />
              <input required placeholder="Last name" className={inputCls} value={f.last_name} onChange={(e) => set("last_name", e.target.value)} data-testid="reg-last" />
            </div>
            <input type="email" required placeholder="Email" className={inputCls} value={f.email} onChange={(e) => set("email", e.target.value)} data-testid="reg-email" />
            <input placeholder="Mobile phone" className={inputCls} value={f.phone} onChange={(e) => set("phone", e.target.value)} />
            <input type="password" required placeholder="Password (8+ chars, upper/lower/number)" className={inputCls} value={f.password} onChange={(e) => set("password", e.target.value)} data-testid="reg-password" />
            <textarea rows={2} placeholder="Why would you love to experience The D?" className={inputCls} value={f.why_join} onChange={(e) => set("why_join", e.target.value)} data-testid="reg-why-join" />
            <input placeholder="Preferred dates (optional)" className={inputCls} value={f.preferred_dates} onChange={(e) => set("preferred_dates", e.target.value)} data-testid="reg-preferred-dates" />
            <input placeholder="Invitation code (optional)" className={inputCls} value={f.invitation_code} onChange={(e) => set("invitation_code", e.target.value.toUpperCase())} data-testid="reg-invite" />
            <p className="text-[11px] text-muted-foreground/70">Your note is shared privately with The D to help us understand your request. It is never shown publicly.</p>
            <button disabled={loading} className="btn-gold w-full py-3 text-sm flex items-center justify-center gap-2" data-testid="reg-submit">
              {loading ? <Loader2 className="animate-spin" size={16} /> : null} {hasInvite ? "Redeem Invitation" : "Request Access"}
            </button>
          </form>
          <p className="text-sm text-muted-foreground text-center mt-6">Already have an account? <Link to="/login" className="text-gold">Sign in</Link></p>
        </div>
      </div>
      <div className="relative hidden lg:block order-1 lg:order-2">
        <img src={SECTION_IMG.interior} alt="" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-ink/60" />
        <div className="relative z-10 h-full flex items-end p-12"><p className="font-serif text-3xl italic text-gold max-w-sm">Your time. Your people. Your experience.</p></div>
      </div>
    </div>
  );
}
